/**
 * src/contexts/AuthContext.jsx
 *
 * Authentication context wired to the server-side auth functions (Section 6).
 * Talks to /auth-login, /auth-register, /auth-refresh, /auth-me. Access/refresh
 * tokens are signed JWTs held by the session helper. In demo mode the same
 * functions transparently return demo tokens, so the UI behaves identically.
 *
 * Provides the API that ProtectedRoute in App.jsx relies on:
 *   isAuthenticated, isLoading, hasRole(role), hasTier(tier).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/config';
import { setSession, clearSession, apiFetch, getAccessToken } from '@/lib/auth/session';
import { roleAtLeast, tierAtLeast } from '@/lib/auth/roles';

const AuthContext = createContext(null);

const FALLBACK = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  hasRole: () => false,
  hasTier: () => false,
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  return ctx || FALLBACK;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on load: if an access token exists, verify it with /auth-me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAccessToken()) { setIsLoading(false); return; }
      try {
        const res = await apiFetch('auth-me');
        if (!cancelled && res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else if (!cancelled) {
          clearSession();
        }
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(api('auth-login'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    setSession(data);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await fetch(api('auth-register'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Registration failed');
    setSession(data);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const hasRole = useCallback((required) => !!user && roleAtLeast(user.role, required), [user]);
  const hasTier = useCallback((required) => !!user && tierAtLeast(user.tier, required), [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    hasRole,
    hasTier,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
